import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { format } from '@r1-frontend/ui-react/components/costs';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';

import { IFaqArticle } from '~/src/api/content/faq';
import { IShopItem } from '~/src/api/content/shop/getShopItems';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import AccumulateCard from '~/src/features/domru-bonus/components/AccCard';
import FaqBlock from '~/src/features/domru-bonus/components/FaqBlock';
import SpendCard from '~/src/features/domru-bonus/components/SpendCard';
import Rules from '~/src/features/domru-bonus/sections/BonusRules';
import FooterForm from '~/src/features/domru-bonus/sections/FooterForm';
import Header from '~/src/features/domru-bonus/sections/Header';
import Partners from '~/src/features/domru-bonus/sections/Partners';
import { TState } from '~/src/store/index';

import { getIsAbnLoyaltyProgramEnabled } from './constants/abnVariants';

import * as ST from './styled';

interface IProps {
    partners: IShopItem[],
    loyaltyIsRegistered: boolean,
    loyaltyIsAvailable: boolean,
    cashback: string,
    faq?: IFaqArticle[],
    companyName: string,
}

const DomruBonus = ({ partners, loyaltyIsRegistered, loyaltyIsAvailable, cashback, faq, companyName }: IProps): JSX.Element => {
    const router = useRouter();

    const isAbnProgramTermsEnabled = useMemo(getIsAbnLoyaltyProgramEnabled, []);

    const isAuth = useSelector((state: TState) => state.auth.isAuth);
    const footerFormRef = useRef<HTMLDivElement>(null);

    const headerClickHandler = useCallback(() => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.loyaltyProgramLanding,
            action: ACTIONS.click,
            label: 'become a member',
        });

        if (isAuth) {
            footerFormRef.current?.scrollIntoView({ behavior: 'smooth',  block: 'center' });
            if (loyaltyIsRegistered) {
                router.push('lk/aboutdomrubonus');
            }
        } else {
            router.push(`${USER_LOGIN_ROUTE}?referrer=/domrubonus`);
        }
    }, []);

    useEffect(() => {
        if (partners.length) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.loyaltyProgramLanding,
                action: ACTIONS.view,
                label: 'partners',
            });
        }
    }, [partners.length]);

    return (
        <ST.Wrapper>
            <Header headerClickHandler={headerClickHandler}/>

            <ST.Grid>
                <ST.Title>Как работает программа лояльности?</ST.Title>
                <ST.CaruselRow>
                    <Rules cashback={cashback} data-test='rules'/>
                </ST.CaruselRow>
            </ST.Grid>

            <ST.Grid>
                <ST.Title>Как копить бонусы?</ST.Title>
                <ST.SectionRow>
                    <AccumulateCard
                        key={'accumulate-card-1'}
                        title={'Пополняйте свой счет своевременно'}
                        text={isAbnProgramTermsEnabled
                            ? `Вы будете получать кешбэк бонусами в размере ${cashback} от абонентской платы при единовременном пополнении счёта на сумму от ${format(3000)}.`
                            : `Вы будете получать от ${cashback} кешбэк бонусами от абонентской платы при своевременном пополнении счета.`
                        }
                        data-test='accumulate-card'
                    />
                    <AccumulateCard
                        key={'accumulate-card-2'}
                        title={isAbnProgramTermsEnabled ? 'Когда начислим кешбэк' : 'Кешбэк\nза покупки'}
                        text={isAbnProgramTermsEnabled
                            ? `Кешбэк начислим пятого числа ежемесячно, если вы своевременно пополните счёт на сумму от ${format(3000)}.`
                            : `За каждую покупку услуг и товаров в Дом.ру мы начисляем вам ${cashback} от суммы покупок.`
                        }
                        data-test='accumulate-card'
                    />
                </ST.SectionRow>
            </ST.Grid>

            <ST.Grid>
                <ST.Title>Как потратить бонусы?</ST.Title>
                <ST.SectionRow>
                    <SpendCard
                        icon={'bonus-purchase.svg'}
                        data-test='spend-card'
                    >
                        Бонусами можно оплатить до&nbsp;100% стоимости абонентской платы.&nbsp;{
                            isAbnProgramTermsEnabled
                                ? <>100&nbsp;бонусов = {format(20)}</>
                                : <>1&nbsp;бонус = {format(1)}</>
                        }.
                    </SpendCard>
                </ST.SectionRow>
            </ST.Grid>

            {partners && !isAbnProgramTermsEnabled &&
                <ST.Grid>
                    <ST.Title>Партнеры программы</ST.Title>
                    {partners.length
                        ?   <ST.CaruselRow>
                                <Partners partners={partners} data-test='partners'/>
                            </ST.CaruselRow>
                        :   <div>Предложения от&nbsp;партнёров {companyName} доступны только участникам программы лояльности</div>
                    }
                </ST.Grid>
            }

            {isAuth && loyaltyIsAvailable && !loyaltyIsRegistered &&
                <ST.Grid>
                    <ST.Title>Зарегистрируйтесь в программе лояльности</ST.Title>
                    <ST.FormRow>
                        <FooterForm scrollRef={footerFormRef} data-test='footer-form'/>
                    </ST.FormRow>
                </ST.Grid>
            }
            <ST.Grid>
                <FaqBlock faq={faq}/>
            </ST.Grid>
        </ST.Wrapper>
    );
};

export default DomruBonus;
